import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CostDisplay } from './ResourceMappingHelper';

interface TotalCostBadgeProps {
	costDisplay: CostDisplay;
}

const sourceColors: Record<string, { bg: string; text: string }> = {
	Azure: { bg: '#E5F6FD', text: '#009BD8' },
	Databricks: { bg: '#FFEDE5', text: '#FF6B35' },
	'Job Cluster': { bg: '#FFEDE5', text: '#FF6B35' },
	'Interactive Cluster': { bg: '#FDF0E5', text: '#E8873A' },
	'DBSQL Cluster': { bg: '#FEEEF0', text: '#E0507A' },
	'Databricks Instance Pool': { bg: '#F3EAFB', text: '#8E4FD6' },
	'All Resources': { bg: '#EFF1FA', text: '#71758F' }
};

const SourceTag = ({ source }: { source: string }) => {
	const { bg, text } = sourceColors[source] ?? sourceColors['All Resources'];
	const sourceLabel = source === 'Databricks Instance Pool' ? 'Databricks' : source;

	return (
		<Box
			component="span"
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				px: 0.75,
				borderRadius: '4px',
				backgroundColor: bg,
				color: text,
				fontFamily: 'Montserrat',
				fontSize: '10px',
				fontWeight: 500,
				lineHeight: '16px',
				letterSpacing: '0.2px'
			}}
		>
			{sourceLabel}
		</Box>
	);
};

const TotalCostBadge = ({ costDisplay }: TotalCostBadgeProps) => {
	const theme = useTheme();
	const colors = theme.palette as any;

	if (!costDisplay) return null;

	const formatCurrency = (value: number) =>
		`$${Number(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

	if (costDisplay.mode === 'multi') {
		const firstRow = costDisplay.items.slice(0, 3);
		const secondRow = costDisplay.items.slice(3);

		const renderRow = (rowItems: typeof costDisplay.items, startIdx: number) => (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
				{rowItems.map((item, idx) => (
					<Box key={item.source} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						{(startIdx + idx) > 0 && <Typography sx={{ fontSize: '12px', color: '#A9ADCB' }}>|</Typography>}
						<SourceTag source={item.source} />
						<Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: colors.text?.primary, textAlign: 'right' }}>
							{formatCurrency(item.total)}
						</Typography>
					</Box>
				))}
			</Box>
		);

		return (
			<Box>
				<Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#71758F', textAlign: 'right' }}>
					Total Cost (YTD)
				</Typography>
				{renderRow(firstRow, 0)}
				{secondRow.length > 0 && renderRow(secondRow, firstRow.length)}
				<Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: colors.text?.primary, textAlign: 'right' }}>
					Total: {formatCurrency(costDisplay.total)}
				</Typography>
			</Box>
		);
	}

	if (costDisplay.mode === 'dual') {
		return (
			<Box>
				<Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#71758F', textAlign: 'right' }}>
					Total Cost (YTD)
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<SourceTag source="Databricks" />
						<Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: colors.text?.primary, textAlign: 'right' }}>
							{formatCurrency(costDisplay.databricks)}
						</Typography>
					</Box>
					<Typography sx={{ fontSize: '12px', color: '#A9ADCB' }}>|</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<SourceTag source="Azure" />
						<Typography sx={{ fontFamily: 'Montserrat', fontSize: '12px', fontWeight: 500, color: colors.text?.primary, textAlign: 'right' }}>
							{formatCurrency(costDisplay.azure)}
						</Typography>
					</Box>
				</Box>
				<Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: colors.text?.primary, textAlign: 'right' }}>
					Total: {formatCurrency(costDisplay.total)}
				</Typography>
			</Box>
		);
	}

	// single-source badge
	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
				<Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: '#71758F', textAlign: 'right' }}>
					Total Cost (YTD)
				</Typography>
				<SourceTag source={costDisplay.source} />
			</Box>
			<Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 500, color: colors.text?.primary, textAlign: 'right' }}>
				{formatCurrency(costDisplay.total)}
			</Typography>
		</Box>
	);
};

export default TotalCostBadge;